import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { loadEstimate } from '../estimate.js';
import { navigate } from '../router.js';
import { since, count } from '../lib/format.js';

/**
 * The Estimate tool's freshness strip (Estimate Tool 2.3), in the place the run banner holds in
 * Forecast. An estimate does not come from a forecast run, so the banner would be a lie here;
 * what this tool needs to state is when the company ratings were last read from Workamajig and
 * how many website projects are currently unusable, which is the one number that decides how
 * much the tool can price.
 *
 * The unusable count is a link, because it is the only number on screen a reader can act on:
 * Data Hygiene is where it gets smaller.
 */
class EstimateStrip extends StoreMixin(LitElement) {
  static styles = css`
    :host {
      display: block;
      background: var(--surface);
      border-bottom: 1px solid var(--border-soft);
    }
    .row {
      display: flex;
      align-items: baseline;
      gap: 8px 22px;
      flex-wrap: wrap;
      padding: 8px var(--page-gutter);
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .lead {
      color: var(--ink);
      font-weight: 600;
    }
    .gap {
      margin-left: auto;
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }
    a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
      cursor: pointer;
    }
    a:hover {
      color: var(--ink-strong);
    }
    .stale {
      color: var(--status-overloaded);
      font-weight: 600;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    loadEstimate();
  }

  _go(event, path) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(path);
  }

  render() {
    const { loading, error, data } = this.appState.estimate;
    if (loading && !data) return html`<div class="row">Loading the client directory…</div>`;
    if (error) return html`<div class="row"><span class="stale">${error}</span></div>`;
    if (!data) return nothing;

    const { ratings, join } = data;
    // Unusable is what the pool is not: every website-project client without both ratings. The
    // hygiene screen counts projects; this counts the clients, because clients are what a
    // reader fixes and one fix can free several projects.
    const unrated = ratings.pool_unrated;
    const readAt = ratings.read_at;

    return html`
      <div class="row">
        <span class="lead">Estimate</span>
        <span>
          Client ratings read ${readAt ? since(readAt) : html`<span class="stale">never</span>`}
          from Workamajig
        </span>
        <span class="gap">
          <span>
            ${count(ratings.pool_both)} of ${count(ratings.pool_companies)} website clients rated
          </span>
          ${
            unrated > 0
              ? html`<span>
                  <a href="/estimate/hygiene" @click=${(e) => this._go(e, '/estimate/hygiene')}
                    >${count(unrated)} unrated</a
                  >, and so unable to price
                </span>`
              : nothing
          }
          ${
            join.unresolved.length
              ? html`<span class="stale">
                  ${count(join.unresolved.length)} client
                  ${join.unresolved.length === 1 ? 'name' : 'names'} resolve to nothing
                </span>`
              : nothing
          }
        </span>
      </div>
    `;
  }
}

customElements.define('estimate-strip', EstimateStrip);
